import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setUser, initialState } from 'store/auth/userSlice'
import { apiSignIn, apiSignOut, apiValidateOTP } from 'services/AuthService'
import { onSignInSuccess, onSignOutSuccess, clearSession } from 'store/auth/sessionSlice'
import appConfig from 'configs/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'
import { Image_BASE_URL, API_BASE_URL } from 'redux/constants'
import { getMinistryAgencyName, getCurrentUserId } from 'utils/userSession'
import apiFetch from 'utils/apiFetch'


function useAuth() {

	const dispatch = useDispatch()

	const navigate = useNavigate()

	const query = useQuery()

	const { token, signedIn } = useSelector((state) => state.auth.session)

	const authenticated = token && signedIn
	
	// Debug authentication state
	React.useEffect(() => {
		console.log('🔐 Auth state - token:', token, 'signedIn:', signedIn, 'authenticated:', authenticated)
	}, [token, signedIn, authenticated])

	// Validate session on mount - only run once
	React.useEffect(() => {
		const validateSession = () => {
			try {
				const storedData = localStorage.getItem('LPA_Admin')
				const currentSignedIn = signedIn
				if (!storedData && currentSignedIn) {
					// Session data is missing but Redux thinks we're signed in
					console.log('Session validation failed - clearing session')
					dispatch(clearSession())
					dispatch(setUser(initialState))
				}
			} catch (error) {
				console.error('Error validating session:', error)
				dispatch(clearSession())
				dispatch(setUser(initialState))
			}
		}

		validateSession()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch])

    const signIn = async ({ userName, password }) => {
        try {
            // Prevent multiple simultaneous sign-in attempts
            if (signedIn) {
                return {
                    status: 'success',
                    message: 'Already signed in'
                }
            }
            
            const response = await apiSignIn({ userName, password })

            if (!response || response.status === false) {
                return {
                    status: 'failed',
                    message: response?.message || ' failed'
                }
            }

            // Check if OTP is required
            if (response.status === true && !response.data1 && response.message && 
                response.message.toLowerCase().includes('otp')) {
                return {
                    status: 'otp_required',
                    message: response.message,
                    email: userName
                }
            }

            const user = response.data1 || {}
            if (!response.token) {
                return {
                    status: 'failed',
                    message: response?.message || 'Login failed — no token received'
                }
            }

            // Use a generic role that has access to routes
            // Menu visibility will be controlled by the dynamic menu data from API
            const role = 'user'
            let businessName = 'LPA Scheme'
            
            // Set business name based on user type
            const userType = (user.userType || '').toLowerCase()
            let authorities = []
            
            if (userType === 'admin' || userType === 'system admin') {
                businessName = 'LPA Scheme Central Admin'
                authorities = ['user', 'admin', 'MinistryAgency', 'Vendor']
            } else if (userType === 'ministryagency' || userType === 'ministry_agency') {
                businessName = user.ministryAgency?.name || 'LPA Scheme Ministry & Agency'
                authorities = ['user', 'MinistryAgency']
            } else if (userType === 'vendor') {
                businessName = user.vendor?.name || 'Vendor'
                authorities = ['user', 'Vendor']
            } else if (userType === 'employee') {
                businessName = 'Employee Portal'
                authorities = ['user', 'Employee']
            } else if (userType === 'bank') {
                businessName = 'Bank Portal'
                authorities = ['user', 'Bank']
            } else {
                // Default authorities
                authorities = ['user', 'admin', 'MinistryAgency', 'Vendor']
            }

            const normalizedUser = {
                id: user.id,
                firstName: user.firstName,
                emailAddress: user.email,
                imagePath: user.image || 'default-avatar.png'
            }

            localStorage.setItem('items', JSON.stringify(normalizedUser))
            localStorage.setItem('business', JSON.stringify({ businessName: businessName }))
            localStorage.setItem('LPA_Admin', JSON.stringify(response))
            
            // Store menu data for dynamic navigation
            if (response.data) {
                localStorage.setItem('LPA_Menu', JSON.stringify(response.data))
            }

            dispatch(setUser({
                avatar: `${Image_BASE_URL}/${normalizedUser.imagePath}`,
                userName: normalizedUser.firstName || normalizedUser.emailAddress,
                businessName: businessName,
                merchantId: normalizedUser.id,
                email: normalizedUser.emailAddress,
                authority: authorities,
                businessId: 1,
                roleName: response.data3 || '',
            }))

            dispatch(onSignInSuccess(response))
            
            // Redirect logic after successful login
            const redirectUrl = query.get(REDIRECT_URL_KEY)
            
            // Check if user is employee or bank and redirect accordingly
            if (userType === 'employee') {
                navigate('/employee-dashboard')
            } else if (userType === 'bank') {
                navigate('/bank-dashboard')
            } else if (redirectUrl) {
                // If there's a redirect URL from the query params, use it
                navigate(redirectUrl)
            } else if (response.data && response.data.length > 0) {
                // If we have dynamic menu data, navigate to first menu item
                const firstMenuItem = response.data[0]
                if (firstMenuItem.subMenu && firstMenuItem.subMenu.length > 0) {
                    navigate(firstMenuItem.subMenu[0].path)
                } else {
                    navigate(firstMenuItem.path)
                }
            } else {
                // No menu data - let the app handle default routing
                // Don't force a navigation, just let React Router handle it
                navigate(appConfig.authenticatedEntryPath)
            }

            return {
                status: 'success',
                message: response.message || 'Login successful'
            }
        } catch (e) {
            return {
                status: 'failed',
                message: 'Unable to sign in'
            }
        }
    }

	const verifyOTP = async ({ email, password, otp }) => {
		try {
			// Prevent multiple simultaneous OTP verification attempts
			if (signedIn) {
				return {
					status: 'success',
					message: 'Already signed in'
				}
			}
			
			const response = await apiValidateOTP({ email, password, otp })

			if (!response || response.status === false) {
				return {
					status: 'failed',
					message: response?.message || 'OTP verification failed'
				}
			}

			const user = response.data1 || {}

			if (!response.token) {
				return {
					status: 'failed',
					message: response?.message || 'OTP verification failed — no token received'
				}
			}

			// Use a generic role that has access to routes
			// Menu visibility will be controlled by the dynamic menu data from API
			const role = 'user'
			let businessName = 'LPA Scheme'
			
			// Set business name based on user type
			const userType = (user.userType || '').toLowerCase()
			let authorities = []
			
			if (userType === 'admin' || userType === 'system admin') {
				businessName = 'LPA Scheme Central Admin'
				authorities = ['user', 'admin', 'MinistryAgency', 'Vendor']
			} else if (userType === 'ministryagency' || userType === 'ministry_agency') {
				businessName = user.ministryAgency?.name || 'LPA Scheme Ministry & Agency'
				authorities = ['user', 'MinistryAgency']
			} else if (userType === 'vendor') {
				businessName = user.vendor?.name || 'Vendor'
				authorities = ['user', 'Vendor']
			} else if (userType === 'employee') {
				businessName = 'Employee Portal'
				authorities = ['user', 'Employee']
			} else if (userType === 'bank') {
				businessName = 'Bank Portal'
				authorities = ['user', 'Bank']
			} else {
				// Default authorities
				authorities = ['user', 'admin', 'MinistryAgency', 'Vendor']
			}

			const normalizedUser = {
				id: user.id,
				firstName: user.firstName,
				emailAddress: user.email,
				imagePath: user.image || 'default-avatar.png'
			}

			localStorage.setItem('items', JSON.stringify(normalizedUser))
			localStorage.setItem('business', JSON.stringify({ businessName: businessName }))
			localStorage.setItem('LPA_Admin', JSON.stringify(response))
			
			// Store menu data for dynamic navigation
			if (response.data) {
				localStorage.setItem('LPA_Menu', JSON.stringify(response.data))
			}

			dispatch(setUser({
				avatar: `${Image_BASE_URL}/${normalizedUser.imagePath}`,
				userName: normalizedUser.firstName || normalizedUser.emailAddress,
				businessName: businessName,
				merchantId: normalizedUser.id,
				email: normalizedUser.emailAddress,
				authority: authorities,
				businessId: 1,
				roleName: response.data3 || '',
			}))

			dispatch(onSignInSuccess(response))
			
			// Redirect logic after successful OTP verification
			const redirectUrl = query.get(REDIRECT_URL_KEY)
			
			// Check if user is employee or bank and redirect accordingly
			if (userType === 'employee') {
				navigate('/employee-dashboard')
			} else if (userType === 'bank') {
				navigate('/bank-dashboard')
			} else if (redirectUrl) {
				// If there's a redirect URL from the query params, use it
				navigate(redirectUrl)
			} else if (response.data && response.data.length > 0) {
				// If we have dynamic menu data, navigate to first menu item
				const firstMenuItem = response.data[0]
				if (firstMenuItem.subMenu && firstMenuItem.subMenu.length > 0) {
					navigate(firstMenuItem.subMenu[0].path)
				} else {
					navigate(firstMenuItem.path)
				}
			} else {
				// No menu data - let the app handle default routing
				navigate(appConfig.authenticatedEntryPath)
			}

			return {
				status: 'success',
				message: response.message || 'Login successful'
			}
		} catch (e) {
			return {
				status: 'failed',
				message: 'Unable to verify OTP'
			}
		}
	}

	const handleSignOut = () => {
		dispatch(onSignOutSuccess())
		dispatch(setUser(initialState))
		navigate(appConfig.unAuthenticatedEntryPath)
		localStorage.removeItem('items')
		localStorage.removeItem('business')
		localStorage.removeItem('LPA_Admin')
		localStorage.removeItem('LPA_Menu')
		window.location.reload()
	}

	const signOut = async () => {
		try {
			const userId = getCurrentUserId()

			if (userId) {
				const logoutUrl = `${API_BASE_URL}Account/LogOut?UserId=${userId}`

				const fetchPromise = apiFetch(logoutUrl, {
					method: 'POST',
					headers: {
						accept: 'text/plain'
					},
					body: '',
					keepalive: true
				})
				.then(async response => {
					console.log('Logout API response status:', response.status)
					if (!response.ok) {
						console.warn('Logout API returned non-OK status:', response.status)
					}
					try {
						const data = await response.json()
						console.log('Logout API response data:', data)
						return data
					} catch (e) {
						console.log('Logout API response is not JSON')
						return {}
					}
				})
				.catch(error => {
					// Silently handle errors - we still want to proceed with logout
					console.error('Logout API call failed:', error)
				})
				
				// Wait for API call with a timeout (max 2 seconds)
				// This ensures the request has time to be sent before page reloads
				await Promise.race([
					fetchPromise,
					new Promise(resolve => setTimeout(resolve, 2000))
				])
			} else {
				console.warn('Cannot call logout API: userId missing', { userId })
			}
		} catch (error) {
			// Silently handle errors - we still want to proceed with logout
			console.error('Error calling logout API:', error)
		}
		
		// Proceed with logout regardless of API call result
		handleSignOut()
	}

	return {
		authenticated,
		signIn,
		signOut,
		verifyOTP
	}
}

export default useAuth